'use client'

import {
  getHomeHeroMobileDisplaySize,
  getHomeHeroMobileN7ImageUrl,
} from '@/components/common/home/hero-banner-image-params'
import { Button } from '@/components/ui/button'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel'
import { cn } from '@/lib/utils'
import type { TBannerSlide } from '@/types/home'
import Autoplay from 'embla-carousel-autoplay'
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react'
import Link from 'next/link'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { HOME_HERO_DESKTOP_SIZE, getHomeHeroDesktopImageUrl } from './hero-image'

const AUTOPLAY_OPTIONS = {
  delay: 3000,
  stopOnInteraction: false,
  stopOnMouseEnter: true,
}

type Props = {
  slides: TBannerSlide[]
  autoplay?: boolean
  autoplayDelay?: number
}

const isSlideActive = (slide: TBannerSlide, now: number) => {
  if (slide.hidden) return false
  if (!slide.active_time) return true
  const start = slide.starts_at ? new Date(slide.starts_at).getTime() : 0
  const end = slide.ends_at ? new Date(slide.ends_at).getTime() : Infinity
  return now > start && now < end
}

export const HomeHeroCarousel = ({
  slides,
  autoplay = true,
  autoplayDelay = 3000,
}: Props) => {
  const validSlides = useMemo(() => {
    const now = Date.now()
    return slides.filter((slide) => isSlideActive(slide, now))
  }, [slides])

  const [api, setApi] = useState<CarouselApi>()
  const [selectedIndex, setSelectedIndex] = useState(0)
  const plugins = useMemo(() => {
    if (!autoplay) return []
    return [
      Autoplay({
        ...AUTOPLAY_OPTIONS,
        delay: autoplayDelay,
      }),
    ]
  }, [autoplay, autoplayDelay])

  useEffect(() => {
    if (!api) return
    const onSelect = () => setSelectedIndex(api.selectedScrollSnap())
    api.on('select', onSelect)
    return () => {
      api.off('select', onSelect)
    }
  }, [api])

  const scrollPrev = useCallback(() => api?.scrollPrev(), [api])
  const scrollNext = useCallback(() => api?.scrollNext(), [api])

  if (!validSlides.length) return null

  const hasMultipleSlides = validSlides.length > 1

  return (
    <section className='relative w-full' aria-label='Hero Banner Slideshow'>
      <Carousel opts={{ loop: true }} plugins={plugins} setApi={setApi}>
        <CarouselContent className='ml-0'>
          {validSlides.map((slide, index) => (
            <CarouselItem
              key={slide.id ?? index}
              className='relative cursor-default p-0'
              aria-label={`${index + 1} / ${validSlides.length}`}
            >
              <HeroSlide
                slide={slide}
                index={index}
                hasControls={hasMultipleSlides}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {hasMultipleSlides && (
        <div className='pointer-events-none absolute inset-x-4 bottom-6 flex items-center gap-6 md:inset-x-auto md:bottom-[86px] md:right-15'>
          <div className='pointer-events-auto flex flex-1 gap-1.5'>
            {validSlides.map((_, index) => (
              <button
                key={index}
                type='button'
                onClick={() => api?.scrollTo(index)}
                className={cn(
                  'h-0.5 flex-1 bg-white transition-opacity md:w-10 md:flex-none',
                  selectedIndex === index ? 'opacity-100' : 'opacity-40'
                )}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>
          <div className='pointer-events-auto flex gap-2 max-md:hidden'>
            <Button
              variant='outline-white'
              size='icon'
              className='size-10 rounded-none'
              onClick={scrollPrev}
              aria-label='Previous slide'
              ga-tracking-value='home__hero-banner__previous-arrow'
              ga-tracking-label='previous_arrow'
            >
              <ChevronLeft />
            </Button>
            <Button
              variant='outline-white'
              size='icon'
              className='size-10 rounded-none'
              onClick={scrollNext}
              aria-label='Next slide'
              ga-tracking-value='home__hero-banner__next-arrow'
              ga-tracking-label='next_arrow'
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}

type HeroSlideProps = {
  slide: TBannerSlide
  index: number
  hasControls: boolean
}

const HeroSlide = ({ slide, index, hasControls }: HeroSlideProps) => {
  const isFirst = index === 0
  const trackingValue = `home__hero-banner__vitri-${index + 1}`
  const label = slide.heading || `Hero banner ${index + 1}`
  const mobileUrl = getHomeHeroMobileN7ImageUrl(slide.mb_image || slide.image)
  const desktopUrl = getHomeHeroDesktopImageUrl(slide.image)
  const mobileSize = getHomeHeroMobileDisplaySize()
  // Chỉ slide chọn kiểu "Tùy chỉnh" trong admin mới hiện text + CTA
  const hasContent =
    slide.display_mode === 'custom' &&
    !!(slide.sub_heading || slide.heading || slide.cta_button_text)
  const ctaUrl = slide.cta_url || slide.url || '#'

  return (
    <div className='relative w-full overflow-hidden'>
      <Link
        href={slide.url || '#'}
        target={slide.link_target}
        className='relative block'
        ga-tracking-value={trackingValue}
        ga-tracking-label={label}
        aria-label={label}
      >
        {/* Native <picture>/<img> để URL khớp với preload LCP ở page.tsx.
            Ảnh giữ tỉ lệ gốc như hero cũ (w-full, cao tự động). */}
        <picture>
          {desktopUrl ? (
            <source
              media='(min-width: 768px)'
              srcSet={desktopUrl}
              width={HOME_HERO_DESKTOP_SIZE.width}
              height={HOME_HERO_DESKTOP_SIZE.height}
            />
          ) : null}
          <img
            src={mobileUrl || desktopUrl}
            alt={label}
            width={mobileSize.width}
            height={mobileSize.height}
            className='block h-auto w-full object-cover'
            loading={isFirst ? 'eager' : 'lazy'}
            fetchPriority={isFirst ? 'high' : 'auto'}
            decoding='async'
          />
        </picture>
        {hasContent && (
          <span
            aria-hidden
            className='absolute inset-0 bg-gradient-to-b from-transparent from-55% to-black/10 md:to-black/55'
          />
        )}
      </Link>

      {hasContent && (
        <div
          className={cn(
            'pointer-events-none absolute inset-x-4 bottom-6 flex flex-col gap-3 text-white md:inset-x-auto md:bottom-[86px] md:left-[75px] md:max-w-[575px] md:gap-6',
            hasControls && 'max-md:bottom-[38px]'
          )}
        >
          <div className='flex flex-col'>
            {slide.sub_heading && (
              <p className='text-base leading-6'>{slide.sub_heading}</p>
            )}
            {slide.heading && (
              <p className='font-criteria text-[28px] font-semibold leading-[41px] tracking--2 md:text-[65px] md:font-medium md:leading-[90px]'>
                {slide.heading}
              </p>
            )}
          </div>
          {slide.cta_button_text && (
            <Button
              asChild
              variant='white'
              className='pointer-events-auto w-full gap-2 md:h-[50px] md:w-fit md:px-10 md:text-base md:[&_svg]:size-6'
            >
              <Link
                href={ctaUrl}
                target={slide.link_target}
                ga-tracking-value={`${trackingValue}__cta`}
                ga-tracking-label={slide.cta_button_text}
              >
                {slide.cta_button_text}
                <ArrowRight strokeWidth={1.5} />
              </Link>
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
